import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  CLEANUP_AGE_MAX_DAYS,
  CLEANUP_AGE_MIN_DAYS,
  CLEANUP_CATEGORIES,
  humanizeApiError,
  filterUtilities,
  isRunActive,
  isRunStale,
  utilityRelativeTime,
  utilityStatusLabel,
  type CleanupCategoryId,
  type GmailCleanupConfig,
  type Utility,
  type UtilityRun,
  type UtilityStatus,
} from '@lapo/core';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * The Utilities hub + Gmail Cleanup detail — a top-level surface (sibling to Integrations) for small
 * on-demand maintenance workloads. The hub is a presentational card grid (mirrors IntegrationsView and
 * reuses its `.lapo-int-*` styling); the detail is a self-contained smart component that fetches its
 * own data + polls the live run, so App.tsx only has to route to it and hand it an API surface.
 */

const STATUS_FILTERS: { id: UtilityStatus | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'paused', label: 'Not connected' },
  { id: 'coming-soon', label: 'Coming soon' },
];

function StatusBadge({ status }: { status: UtilityStatus }): ReactElement {
  return (
    <span className={`lapo-int-badge lapo-int-badge-${status}`} data-testid="utility-status">
      <span className="lapo-int-dot" aria-hidden />
      {utilityStatusLabel(status)}
    </span>
  );
}

function ActivityLine({ utility }: { utility: Utility }): ReactElement {
  const a = utility.lastAction;
  if (!a) return <span className="lapo-int-activity lapo-int-activity-none">No activity yet</span>;
  return (
    <span className={`lapo-int-activity${a.ok ? '' : ' lapo-int-activity-fail'}`}>
      <MaterialIcon name={a.ok ? 'check_circle' : 'error'} size={14} />
      <span className="lapo-int-activity-text">{a.summary}</span>
      <span className="lapo-int-activity-time">· {utilityRelativeTime(a.agoMinutes)}</span>
    </span>
  );
}

function UtilityCard({ utility, onOpen }: { utility: Utility; onOpen: (id: string) => void }): ReactElement {
  const disabled = utility.status === 'coming-soon';
  return (
    <div className="lapo-int-card" data-testid="utility-card" data-status={utility.status}>
      <button
        type="button"
        className="lapo-int-card-main"
        onClick={() => !disabled && onOpen(utility.id)}
        disabled={disabled}
        aria-label={`Open ${utility.name}`}
      >
        <div className="lapo-int-card-head">
          <span className="lapo-int-icon">
            <MaterialIcon name={utility.icon} size={22} />
          </span>
          <span className="lapo-int-name">{utility.name}</span>
          <StatusBadge status={utility.status} />
        </div>
        <p className="lapo-int-desc">{utility.description}</p>
        <div className="lapo-int-card-foot">
          <ActivityLine utility={utility} />
        </div>
      </button>
    </div>
  );
}

export interface UtilitiesViewProps {
  utilities: Utility[];
  onOpen: (id: string) => void;
}

export const UtilitiesView = memo(UtilitiesViewBase);

function UtilitiesViewBase({ utilities, onOpen }: UtilitiesViewProps): ReactElement {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<UtilityStatus | 'all'>('all');
  const visible = useMemo(() => filterUtilities(utilities, { query, status }), [utilities, query, status]);

  return (
    <div className="lapo-integrations">
      <header className="lapo-int-head">
        <h1 className="lapo-int-title">Utilities</h1>
        <p className="lapo-int-subtitle">
          Small maintenance chores you run on demand — cleaning up mail, calendars, and more.
        </p>
      </header>

      <div className="lapo-int-filter">
        <div className="lapo-int-search">
          <MaterialIcon name="search" size={20} />
          <input
            type="text"
            className="lapo-int-search-input"
            placeholder="Filter utilities…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter utilities"
          />
          {query && (
            <button type="button" className="lapo-int-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <MaterialIcon name="close" size={18} />
            </button>
          )}
        </div>
        <div className="lapo-int-chips" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`lapo-int-chip${f.id === status ? ' lapo-active' : ''}`}
              aria-pressed={f.id === status}
              onClick={() => setStatus(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {visible.length ? (
        <div className="lapo-int-grid" data-testid="utility-grid">
          {visible.map((u) => (
            <UtilityCard key={u.id} utility={u} onOpen={onOpen} />
          ))}
        </div>
      ) : (
        <div className="lapo-int-empty">
          <MaterialIcon name="filter_alt_off" size={28} />
          <p>No utilities match your filter.</p>
        </div>
      )}
    </div>
  );
}

// ─── Gmail Cleanup detail ──────────────────────────────────────────────────────────────────────────

/** One linked Gmail mailbox row on the detail page. */
export interface UtilityAccountRow {
  accountId: string;
  email: string;
  enabled: boolean;
  hasDeletePermission: boolean;
}

export interface UtilityDetailData {
  config: GmailCleanupConfig;
  accounts: UtilityAccountRow[];
  lastRun: UtilityRun | null;
}

/** The API surface the detail needs — structurally satisfied by the app's apiClient (kept UI-local so ui doesn't depend on app). */
export interface UtilitiesApi {
  getUtilityDetail(id: string): Promise<UtilityDetailData>;
  putUtilityConfig(id: string, config: GmailCleanupConfig): Promise<GmailCleanupConfig>;
  getUtilityStatus(id: string): Promise<UtilityRun | null>;
  previewUtility(id: string): Promise<UtilityRun>;
  runUtility(id: string, approveRunId?: string): Promise<UtilityRun>;
}

export interface UtilityDetailProps {
  utilityId: string;
  api: UtilitiesApi;
  onBack: () => void;
  /** Begin the separate "Grant delete permission" Google re-consent, then resolve once the grant lands. */
  onGrantPermission: () => Promise<void>;
  /** Surface a toast (optional; falls back to inline error text). */
  onError?: (message: string) => void;
  /** Poll interval for the live run (ms). Injectable so tests can drive it fast. */
  pollMs?: number;
}

const CATEGORY_LABEL: Record<CleanupCategoryId, string> = {
  calendarResponses: CLEANUP_CATEGORIES[0]!.label,
  fileShares: CLEANUP_CATEGORIES[1]!.label,
};

export const UtilityDetail = memo(UtilityDetailBase);

function UtilityDetailBase({ utilityId, api, onBack, onGrantPermission, onError, pollMs = 1500 }: UtilityDetailProps): ReactElement {
  const [data, setData] = useState<UtilityDetailData | null>(null);
  const [run, setRun] = useState<UtilityRun | null>(null);
  const [draft, setDraft] = useState<GmailCleanupConfig | null>(null);
  const [busy, setBusy] = useState<'preview' | 'run' | 'save' | 'grant' | null>(null);
  const [dismissedPreview, setDismissedPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = useCallback(
    (e: unknown) => {
      const msg = humanizeApiError(e);
      setError(msg);
      onError?.(msg);
    },
    [onError],
  );

  const load = useCallback(async () => {
    try {
      const d = await api.getUtilityDetail(utilityId);
      setData(d);
      setDraft(d.config);
      setRun(d.lastRun);
    } catch (e) {
      fail(e);
    }
  }, [api, utilityId, fail]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll the live run while it's active (previewing/executing) — but treat a run whose doc has gone
  // quiet as stalled (its owning request died mid-scan) so we stop polling a dead run and re-enable a
  // retry instead of spinning on "Scanning…" forever.
  const stalled = run ? isRunStale(run, Date.now()) : false;
  const runActive = run ? isRunActive(run.phase) && !stalled : false;
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    if (!runActive) return;
    let alive = true;
    const tick = async () => {
      try {
        const latest = await api.getUtilityStatus(utilityId);
        if (alive && latest) setRun(latest);
      } catch {
        /* transient poll error — keep trying */
      }
    };
    const h = setInterval(() => void tick(), pollMs);
    return () => {
      alive = false;
      clearInterval(h);
    };
  }, [runActive, api, utilityId, pollMs]);

  const setDraftField = useCallback(<K extends keyof GmailCleanupConfig>(key: K, value: GmailCleanupConfig[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }, []);

  const saveConfig = useCallback(async () => {
    if (!draft) return;
    setBusy('save');
    try {
      const saved = await api.putUtilityConfig(utilityId, draft);
      setDraft(saved);
      setData((d) => (d ? { ...d, config: saved } : d));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }, [api, draft, utilityId, fail]);

  const doPreview = useCallback(async () => {
    setBusy('preview');
    setError(null);
    setDismissedPreview(false);
    // Persist the current settings first so the preview reflects them.
    if (draft) {
      try {
        await api.putUtilityConfig(utilityId, draft);
      } catch (e) {
        fail(e);
        setBusy(null);
        return;
      }
    }
    // Kick off the scan but DON'T block the panel on the whole thing finishing — for a large mailbox
    // that made the UI look frozen. The server writes a 'previewing' run doc up front; prime it so the
    // /status poll drives live progress, and swap in the final result when the request settles.
    const running = api
      .previewUtility(utilityId)
      .then((r) => setRun(r))
      .catch((e) => fail(e))
      .finally(() => setBusy(null));
    try {
      const started = await api.getUtilityStatus(utilityId);
      if (started) setRun(started);
    } catch {
      /* the poll effect will pick the run up on its next tick */
    }
    await running;
  }, [api, draft, utilityId, fail]);

  const doRun = useCallback(
    async (approveRunId?: string) => {
      setBusy('run');
      setError(null);
      // Same as preview: let the /status poll show live trashing progress rather than blocking the
      // panel on the whole execute finishing.
      const running = api
        .runUtility(utilityId, approveRunId)
        .then((r) => setRun(r))
        .catch((e) => fail(e));
      try {
        const started = await api.getUtilityStatus(utilityId);
        if (started) setRun(started);
      } catch {
        /* the poll effect will pick the run up on its next tick */
      }
      try {
        await running;
        await load(); // refresh config (previewApproved may have flipped) + last run
      } finally {
        setBusy(null);
      }
    },
    [api, utilityId, fail, load],
  );

  const doGrant = useCallback(async () => {
    setBusy('grant');
    try {
      await onGrantPermission();
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }, [onGrantPermission, load, fail]);

  if (!data || !draft) {
    return (
      <div className="lapo-int-detail" data-testid="utility-detail-loading">
        <div className="lapo-int-detail-bar">
          <button type="button" className="lapo-int-back" onClick={onBack} aria-label="Back to utilities">
            <MaterialIcon name="arrow_back" size={18} />
            Utilities
          </button>
        </div>
        <p className="lapo-int-empty-inline">Loading…</p>
      </div>
    );
  }

  const trusted = data.config.previewApproved;
  const awaitingApproval = run?.phase === 'awaiting-approval' && !dismissedPreview;
  const anyEnabled = draft.accounts && Object.values(draft.accounts).some((a) => a.enabled);

  return (
    <div className="lapo-int-detail" data-testid="utility-detail">
      <div className="lapo-int-detail-bar">
        <button type="button" className="lapo-int-back" onClick={onBack} aria-label="Back to utilities">
          <MaterialIcon name="arrow_back" size={18} />
          Utilities
        </button>
        <div className="lapo-util-actions">
          <button
            type="button"
            className="lapo-int-run lapo-util-preview"
            onClick={() => void doPreview()}
            disabled={busy !== null || runActive || !anyEnabled}
            aria-label="Preview cleanup"
          >
            <MaterialIcon name="visibility" size={16} />
            {busy === 'preview' ? 'Previewing…' : 'Preview'}
          </button>
          {trusted && (
            <button
              type="button"
              className="lapo-int-run"
              onClick={() => void doRun()}
              disabled={busy !== null || runActive || !anyEnabled}
              aria-label="Run cleanup now"
            >
              <MaterialIcon name={runActive ? 'progress_activity' : 'play_arrow'} size={16} />
              {busy === 'run' || runActive ? 'Running…' : 'Run now'}
            </button>
          )}
        </div>
      </div>

      <header className="lapo-int-detail-head">
        <span className="lapo-int-icon lapo-int-icon-lg">
          <MaterialIcon name="mop" size={30} />
        </span>
        <div className="lapo-int-detail-headtext">
          <div className="lapo-int-detail-titlerow">
            <h1 className="lapo-int-detail-title">Gmail Cleanup</h1>
          </div>
          <p className="lapo-int-detail-desc">
            Moves calendar invite responses and Google file-share notices to Trash (recoverable for 30 days),
            so they stop cluttering your search.
          </p>
        </div>
      </header>

      {error && <p className="lapo-set-error" role="alert">{error}</p>}

      {awaitingApproval && run && (
        <PreviewApproval run={run} busy={busy === 'run'} onApprove={() => void doRun(run.runId)} onDismiss={() => setDismissedPreview(true)} />
      )}

      {run && !awaitingApproval && (run.phase === 'queued' || run.phase === 'executing' || run.phase === 'previewing' || run.phase === 'done' || run.phase === 'failed') && (
        <RunProgress run={run} stalled={stalled} />
      )}

      <section className="lapo-int-section" aria-label="Mailboxes">
        <h2 className="lapo-int-section-title">Mailboxes</h2>
        {data.accounts.length === 0 ? (
          <p className="lapo-int-empty-inline">Link a Google account to clean up its mail.</p>
        ) : (
          <ul className="lapo-util-accounts">
            {data.accounts.map((acct) => (
              <li key={acct.accountId} className="lapo-util-account" data-testid="utility-account">
                <label className="lapo-util-account-toggle">
                  <input
                    type="checkbox"
                    checked={draft.accounts[acct.accountId]?.enabled ?? false}
                    onChange={(e) =>
                      setDraftField('accounts', {
                        ...draft.accounts,
                        [acct.accountId]: { enabled: e.target.checked },
                      })
                    }
                    aria-label={`Clean up ${acct.email}`}
                  />
                  <span className="lapo-util-account-email">{acct.email}</span>
                </label>
                {acct.hasDeletePermission ? (
                  <span className="lapo-util-perm-ok">
                    <MaterialIcon name="check_circle" size={14} /> Delete permission granted
                  </span>
                ) : (
                  <button
                    type="button"
                    className="lapo-set-btn is-primary lapo-util-grant"
                    onClick={() => void doGrant()}
                    disabled={busy !== null}
                    aria-label={`Grant delete permission for ${acct.email}`}
                  >
                    {busy === 'grant' ? 'Opening…' : 'Grant delete permission'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="lapo-int-section" aria-label="Settings">
        <h2 className="lapo-int-section-title">Settings</h2>
        <div className="lapo-util-setting">
          <label className="lapo-util-age" htmlFor="lapo-util-age-input">
            <span className="lapo-util-age-label">Delete older than</span>
            <input
              id="lapo-util-age-input"
              type="range"
              min={CLEANUP_AGE_MIN_DAYS}
              max={CLEANUP_AGE_MAX_DAYS}
              value={draft.olderThanDays}
              onChange={(e) => setDraftField('olderThanDays', Number(e.target.value))}
              aria-label="Delete older than (days)"
            />
            <span className="lapo-util-age-value">{draft.olderThanDays} days</span>
          </label>
        </div>
        <div className="lapo-util-categories">
          {CLEANUP_CATEGORIES.map((c) => (
            <label key={c.id} className="lapo-util-category">
              <input
                type="checkbox"
                checked={draft.categories[c.id]}
                onChange={(e) => setDraftField('categories', { ...draft.categories, [c.id]: e.target.checked })}
                aria-label={c.label}
              />
              <span className="lapo-util-category-text">
                <span className="lapo-util-category-label">{c.label}</span>
                <span className="lapo-util-category-desc">{c.description}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="lapo-util-save">
          <button type="button" className="lapo-set-btn" onClick={() => void saveConfig()} disabled={busy !== null}>
            {busy === 'save' ? 'Saving…' : 'Save settings'}
          </button>
          {!trusted && (
            <span className="lapo-util-trust-note">
              <MaterialIcon name="shield" size={14} /> First run shows a preview to approve before anything is trashed.
            </span>
          )}
        </div>
      </section>
    </div>
  );
}

/** The grouped counts + samples approval panel shown after a preview (D11). */
function PreviewApproval({
  run,
  busy,
  onApprove,
  onDismiss,
}: {
  run: UtilityRun;
  busy: boolean;
  onApprove: () => void;
  onDismiss: () => void;
}): ReactElement {
  return (
    <section className="lapo-util-preview-panel" aria-label="Preview" data-testid="utility-preview">
      <div className="lapo-util-preview-head">
        <MaterialIcon name="visibility" size={18} />
        <span>
          Preview — <strong>{run.totals.matched}</strong> {run.totals.matched === 1 ? 'email' : 'emails'} would move to Trash
        </span>
      </div>
      {run.perAccount.map((a) => {
        const pending = run.pendingByAccount?.[a.accountId];
        return (
          <div key={a.accountId} className="lapo-util-preview-account">
            <div className="lapo-util-preview-account-head">{a.email}</div>
            {(Object.keys(a.byCategory) as (keyof typeof a.byCategory)[])
              .filter((cat) => a.byCategory[cat].matched > 0)
              .map((cat) => (
                <div key={cat} className="lapo-util-preview-cat">
                  <span className="lapo-util-preview-cat-name">{CATEGORY_LABEL[cat]}</span>
                  <span className="lapo-util-preview-cat-count">{a.byCategory[cat].matched}</span>
                  <ul className="lapo-util-preview-samples">
                    {(pending?.samples ?? [])
                      .filter((s) => s.category === cat)
                      .slice(0, 4)
                      .map((s, i) => (
                        <li key={i} className="lapo-util-preview-sample" title={s.from}>
                          {s.subject}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            {a.error && <div className="lapo-util-preview-flag">{permLabel(a.error)}</div>}
          </div>
        );
      })}
      <div className="lapo-util-preview-actions">
        <button type="button" className="lapo-int-run" onClick={onApprove} disabled={busy || run.totals.matched === 0} aria-label="Approve and trash">
          <MaterialIcon name={busy ? 'progress_activity' : 'delete'} size={16} />
          {busy ? 'Trashing…' : `Approve & trash ${run.totals.matched}`}
        </button>
        <button type="button" className="lapo-set-btn" onClick={onDismiss} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}

/** Live progress + final summary (per account / per category counts). */
function RunProgress({ run, stalled = false }: { run: UtilityRun; stalled?: boolean }): ReactElement {
  const active = isRunActive(run.phase) && !stalled;
  return (
    <section className="lapo-util-progress" aria-label="Run status" data-testid="utility-progress">
      <div className="lapo-util-progress-head">
        {active ? <MaterialIcon name="progress_activity" size={16} className="lapo-spin" /> : <MaterialIcon name={run.phase === 'failed' || stalled ? 'error' : 'check_circle'} size={16} />}
        <span data-testid="utility-progress-summary">
          {stalled
            ? 'Scan stalled — press Preview to try again.'
            : run.phase === 'done'
              ? `Trashed ${run.totals.trashed} of ${run.totals.matched} matched`
              : run.phase === 'failed'
                ? run.error ?? 'Run failed'
                : run.phase === 'queued'
                  ? 'Queued… starting on the server'
                  : `Scanning… ${run.totals.matched} matched so far`}
        </span>
      </div>
      <ul className="lapo-util-progress-accounts">
        {run.perAccount.map((a) => (
          <li key={a.accountId} className="lapo-util-progress-account">
            <span className="lapo-util-progress-email">{a.email}</span>
            <span className="lapo-util-progress-counts">
              {a.scanned} scanned · {a.matched} matched · {a.trashed} trashed
              {a.error && <span className="lapo-util-progress-flag"> · {permLabel(a.error)}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function permLabel(error: string): string {
  if (error === 'needs-permission') return 'Needs delete permission';
  if (error === 'capped') return 'Too many matches — capped this run';
  if (error === 'interrupted') return 'Interrupted — run again to finish';
  return error;
}
